import React, { useState, useEffect } from 'react';
import { Audio } from '../game/audio';
import { INCUBATOR_MS, incubatorRemaining, incubatorSpeedCost } from '../store';
import { incubatorSlots, incubatorSpeedMult } from '../game/base';
import GameIcon from './GameIcon';
import { fireClaimFx, CAMP_CLAIM_FLY } from './ClaimFx';

// ─── Pet Definitions ─────────────────────────────────────────────────────────
export const PET_TYPES = [
  {
    id: 'aquaSprite', name: 'Aqua Sprite', icon: 'ui_waterdrop', type: 'Attack',
    desc: 'Fires a water bolt at the nearest enemy every 3s.',
    stat: 'dmg', statPerLevel: 0.001, // +0.1% dmg per level
    eggCosts: { common: 50, uncommon: 120, rare: 300, epic: 700, legendary: 1500 },
  },
  {
    id: 'shadowWisp', name: 'Shadow Wisp', icon: 'ui_ghost', type: 'Defend',
    desc: 'Reduces incoming damage (increases dodge).',
    stat: 'dodge', statPerLevel: 0.001,
    eggCosts: { common: 50, uncommon: 120, rare: 300, epic: 700, legendary: 1500 },
  },
  {
    id: 'emberFairy', name: 'Ember Fairy', icon: 'ui_flame', type: 'Crit',
    desc: 'Boosts your critical hit chance.',
    stat: 'crit', statPerLevel: 0.001,
    eggCosts: { common: 60, uncommon: 140, rare: 350, epic: 800, legendary: 1800 },
  },
  {
    id: 'stormHawk', name: 'Storm Hawk', icon: 'ui_bolt', type: 'Super',
    desc: 'Launches a super strike every 10s dealing massive AoE damage.',
    stat: 'superCrit', statPerLevel: 0.002,
    eggCosts: { common: 80, uncommon: 180, rare: 450, epic: 1000, legendary: 2000 },
  },
  {
    id: 'lifeSprite', name: 'Life Sprite', icon: 'ui_leaf', type: 'Heal',
    desc: 'Regenerates your HP over time.',
    stat: 'regen', statPerLevel: 0.01, // +0.01 HP/s per level
    eggCosts: { common: 60, uncommon: 140, rare: 350, epic: 800, legendary: 1800 },
  },
];

const RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythical'];
const RARITY_COLORS = {
  common: '#9a8fa6', uncommon: '#4dff91', rare: '#4dc4ff',
  epic: '#b362ff', legendary: '#ff7a1a', mythical: '#ff3146',
};
// Breeding: 2 Adult+ pets of the same species produce a new egg one
// rarity tier above the HIGHER parent (capped at mythical) — a real
// breeding system distinct from the egg-merge shortcut above (which only
// combines still-unhatched eggs). Each parent needs a real-time cooldown
// before breeding again so it can't be spammed.
const BREED_MIN_LEVEL = 31; // Adult stage (see STAGES below)
const BREED_COOLDOWN_MS = 30 * 60 * 1000; // 30 minutes
const BREED_GEM_COST = { common: 80, uncommon: 160, rare: 350, epic: 800, legendary: 2000 };

// Growth stages. These used to be labels only — a pet's contribution came
// purely from its level, so "Adult" meant nothing mechanically. Each stage
// now carries a multiplier on the pet's stat, which is what makes feeding it
// toward the next stage a goal rather than a number going up.
export const STAGES = [
  { name: 'Newborn',  minLvl: 1,  icon: 'ui_egg',       sizeClass: 'pet-xs',  mult: 1.00 },
  { name: 'Young',    minLvl: 6,  icon: 'ui_hatchling', sizeClass: 'pet-sm',  mult: 1.18 },
  { name: 'Teenage',  minLvl: 16, icon: 'ui_hatchling', sizeClass: 'pet-md',  mult: 1.42 },
  { name: 'Adult',    minLvl: 31, icon: 'ui_star',      sizeClass: 'pet-lg',  mult: 1.75 },
  { name: 'Veteran',  minLvl: 51, icon: 'ui_star',      sizeClass: 'pet-xl',  mult: 2.15 },
  { name: 'Elder',    minLvl: 76, icon: 'ui_star',      sizeClass: 'pet-xl',  mult: 2.60 },
  { name: 'Max',      minLvl: 100, icon: 'ui_infinity', sizeClass: 'pet-max', mult: 3.20 },
];

export function getStage(level) {
  return [...STAGES].reverse().find(s => level >= s.minLvl) || STAGES[0];
}

// Multiplier the pet's active stat gets from its growth stage. Exported so
// buildMetaEffects applies exactly the same curve the panel displays —
// the two drifting apart is how a stat ends up lying to the player.
export function petStageMult(level) {
  return getStage(level).mult || 1;
}

function xpToLevel(level) {
  return Math.round(100 + 20 * Math.pow(level, 1.15));
}

// 3 eggs of same rarity → 1 of next rarity
function canMergeEggs(eggs, rarity) {
  return (eggs.filter(e => e.rarity === rarity).length) >= 3;
}

function mergeEggs(eggs, rarity, petType) {
  const nextIdx = RARITIES.indexOf(rarity) + 1;
  if (nextIdx >= RARITIES.length) return eggs;
  const newEggs = [...eggs];
  let removed = 0;
  for (let i = newEggs.length - 1; i >= 0 && removed < 3; i--) {
    if (newEggs[i].rarity === rarity && newEggs[i].type === petType) { newEggs.splice(i, 1); removed++; }
  }
  newEggs.push({ rarity: RARITIES[nextIdx], type: petType, id: Date.now() + '' });
  return newEggs;
}

export default function PetPanel({ save, setSave, onClose, onOpenTcg }) {
  const [view, setView] = useState('my_pets'); // 'my_pets' | 'shop' | 'eggs' | 'breed'
  const [breedPair, setBreedPair] = useState([]); // up to 2 pet ids
  // Drives the incubator countdowns. A second is fine — these are 15-100
  // minute timers, and a faster tick would just re-render the panel for
  // nothing.
  const [now, setNow] = useState(Date.now());
  const [gainLine, setGainLine] = useState('');
  const showGain = (text, el, bag) => {
    setGainLine(text);
    setTimeout(() => setGainLine((cur) => (cur === text ? '' : cur)), 1100);
    if (el && bag) fireClaimFx(el, bag, { flyTo: CAMP_CLAIM_FLY });
  };
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const pets = save.pets || [];
  const eggs = save.petEggs || [];
  const gems = save.gems || 0;
  const petFood = save.petFood || 0;

  // ----- Breeding -----
  const canBreedPet = (pet) => pet.level >= BREED_MIN_LEVEL && (!pet.breedReadyAt || Date.now() >= pet.breedReadyAt);
  const toggleBreedPick = (pet) => {
    if (!canBreedPet(pet)) return;
    setBreedPair(cur => {
      if (cur.includes(pet.id)) return cur.filter(id => id !== pet.id);
      if (cur.length >= 2) return cur;
      const next = [...cur, pet.id];
      // Only allow pairing same species — mirrors the egg-merge rule above.
      if (next.length === 2) {
        const a = pets.find(p => p.id === next[0]), b = pets.find(p => p.id === next[1]);
        if (a && b && a.type !== b.type) return cur;
      }
      return next;
    });
  };
  const breedResultRarity = () => {
    if (breedPair.length !== 2) return null;
    const a = pets.find(p => p.id === breedPair[0]), b = pets.find(p => p.id === breedPair[1]);
    if (!a || !b) return null;
    const idx = Math.max(RARITIES.indexOf(a.rarity), RARITIES.indexOf(b.rarity));
    return RARITIES[Math.min(RARITIES.length - 1, idx + 1)];
  };
  const doBreed = () => {
    if (breedPair.length !== 2) return;
    const a = pets.find(p => p.id === breedPair[0]), b = pets.find(p => p.id === breedPair[1]);
    if (!a || !b) return;
    const resultRarity = breedResultRarity();
    const cost = BREED_GEM_COST[a.rarity] || 80;
    if (gems < cost) return;
    const readyAt = Date.now() + BREED_COOLDOWN_MS;
    const updPets = pets.map(p => (p.id === a.id || p.id === b.id) ? { ...p, breedReadyAt: readyAt } : p);
    const newEgg = { id: Date.now() + '_' + Math.random().toString(36).slice(2), type: a.type, rarity: resultRarity };
    setSave(cur => {
      if ((cur.gems || 0) < cost) return cur;
      return { ...cur, gems: (cur.gems || 0) - cost, pets: updPets, petEggs: [...(cur.petEggs || []), newEgg] };
    });
    setBreedPair([]);
    showGain(`${resultRarity} egg`, null, null);
    Audio.levelUp(); Audio.claimPing();
  };

  // ----- Incubator -----
  // Eggs no longer hatch on a tap. They go into an incubator slot, run down a
  // rarity-scaled timer that keeps ticking while the game is closed (see
  // tickIdle in store.js), and only then can be cracked open.
  const incSlots = save.incubator?.slots || [];
  // Slot count and hatch speed both come from the Incubator building, read
  // live rather than mirrored into the save — a mirrored value is a value
  // that can disagree with the building that granted it.
  const maxIncSlots = incubatorSlots(save);

  const incubate = (egg) => {
    if (incSlots.length >= maxIncSlots) return;
    const durMs = Math.round((INCUBATOR_MS[egg.rarity] || INCUBATOR_MS.common) * incubatorSpeedMult(save));
    const slot = {
      eggId: egg.id, rarity: egg.rarity, type: egg.type,
      startMs: Date.now(), durMs, spentMs: 0, ready: false,
    };
    setSave(cur => ({
      ...cur,
      petEggs: (cur.petEggs || []).filter(e => e.id !== egg.id),
      incubator: { ...(cur.incubator || {}), slots: [...((cur.incubator && cur.incubator.slots) || []), slot] },
    }));
    showGain('In the incubator');
    Audio.click();
  };

  const speedUp = (slot, kind) => {
    const cost = incubatorSpeedCost(slot, now);
    if (kind === 'gems' && (save.gems || 0) < cost.gems) return;
    if (kind === 'gold' && (save.gold || 0) < cost.gold) return;
    setSave(cur => {
      if (kind === 'gems' && (cur.gems || 0) < cost.gems) return cur;
      if (kind === 'gold' && (cur.gold || 0) < cost.gold) return cur;
      const ns = { ...cur };
      if (kind === 'gems') ns.gems = (cur.gems || 0) - cost.gems;
      else ns.gold = (cur.gold || 0) - cost.gold;
      const slots = (cur.incubator && cur.incubator.slots) || [];
      ns.incubator = {
        ...(cur.incubator || {}),
        slots: slots.map(s2 => s2.eggId === slot.eggId
          ? { ...s2, spentMs: (s2.spentMs || 0) + incubatorRemaining(s2, now), ready: true }
          : s2),
      };
      return ns;
    });
    showGain('Ready to crack');
    Audio.claimPing();
  };

  // Three taps to crack the shell — the payoff moment, not a silent swap.
  const [cracking, setCracking] = useState({});
  const tapShell = (slot) => {
    if (!slot.ready) return;
    const n = (cracking[slot.eggId] || 0) + 1;
    if (n < 3) {
      setCracking({ ...cracking, [slot.eggId]: n });
      Audio.click();
      return;
    }
    setCracking(c => { const c2 = { ...c }; delete c2[slot.eggId]; return c2; });
    hatchFromSlot(slot);
  };

  const hatchFromSlot = (slot) => {
    const petDef = PET_TYPES.find(p => p.id === slot.type);
    if (!petDef) return;
    const newPet = {
      id: Date.now() + '_' + Math.random().toString(36).slice(2),
      type: slot.type, rarity: slot.rarity, level: 1, xp: 0,
      stage: 'Newborn', active: pets.length === 0,
      name: `${petDef.name} (${slot.rarity})`,
    };
    setSave(cur => ({
      ...cur,
      pets: [...(cur.pets || []), newPet],
      incubator: {
        ...(cur.incubator || {}),
        slots: ((cur.incubator && cur.incubator.slots) || []).filter(s2 => s2.eggId !== slot.eggId),
      },
    }));
    showGain(`${newPet.name} hatched`);
    Audio.levelUp(); Audio.claimPing();
  };

  // (The old direct egg->pet hatch lived here. Every egg goes through the
  // incubator now, so it had no callers left — see incubate/hatchFromSlot.)

  // ----- Feed pet -----
  const feedPet = (pet) => {
    if (petFood <= 0) return;
    const xpGain = 150 + pet.level * 10;
    let newXp = pet.xp + xpGain;
    let newLevel = pet.level;
    while (newLevel < 100 && newXp >= xpToLevel(newLevel)) {
      newXp -= xpToLevel(newLevel);
      newLevel++;
      Audio.claimPing();
    }
    const stage = getStage(newLevel);
    setSave(cur => {
      if ((cur.petFood || 0) <= 0) return cur;
      return {
        ...cur,
        pets: (cur.pets || []).map(p => p.id === pet.id ? { ...p, xp: newXp, level: newLevel, stage: stage.name } : p),
        petFood: (cur.petFood || 0) - 1,
      };
    });
    showGain(newLevel > pet.level ? `Level ${newLevel} · +${xpGain} XP` : `+${xpGain} XP`);
    Audio.click();
  };

  // ----- Toggle active -----
  const toggleActive = (pet) => {
    setSave(cur => ({
      ...cur,
      pets: (cur.pets || []).map(p => ({ ...p, active: p.id === pet.id ? !p.active : false })),
    }));
    Audio.click();
  };

  // ----- Buy egg -----
  const buyEgg = (type, rarity) => {
    const def = PET_TYPES.find(p => p.id === type);
    if (!def) return;
    const cost = def.eggCosts[rarity];
    if (gems < cost) return;
    const newEgg = { id: Date.now() + '', type, rarity };
    setSave(cur => {
      if ((cur.gems || 0) < cost) return cur;
      return { ...cur, gems: (cur.gems || 0) - cost, petEggs: [...(cur.petEggs || []), newEgg] };
    });
    showGain(`${rarity} ${def.name} egg`);
    Audio.click();
  };

  // ----- Merge eggs -----
  const doMerge = (rarity, type) => {
    if (!canMergeEggs(eggs.filter(e => e.type === type), rarity)) return;
    setSave(cur => ({ ...cur, petEggs: mergeEggs(cur.petEggs || [], rarity, type) }));
    showGain('Eggs merged');
    Audio.levelUp();
  };

  // ----- Buy pet food -----
  const buyFood = () => {
    if (gems < 50) return;
    setSave(cur => {
      if ((cur.gems || 0) < 50) return cur;
      return { ...cur, gems: (cur.gems || 0) - 50, petFood: (cur.petFood || 0) + 5 };
    });
    showGain('+5 pet food');
    Audio.click();
  };

  return (
    <div className="modal-overlay" data-testid="pet-panel" onClick={(e) => {
      if (e.target.classList.contains('modal-overlay')) onClose();
    }}>
      {/* `pet-panel` is a theme hook. The shell is shared with the
          Weaponsmith, which now carries an ember treatment — right for a
          forge, wrong for a menagerie. One structure, re-tinted per screen,
          rather than a second copy of the same panel CSS. */}
      <div className="forge-panel pet-panel" style={{ maxWidth: 700, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div className="forge-header">
          <div className="forge-title"><GameIcon name="ui_pets" size={18} /> PETS</div>
          <div style={{ fontFamily:'VT323', fontSize:14, color:'var(--ink-dim)', display:'flex', gap:12, alignItems:'center' }}>
            <span style={{ color:'#4dffd4', display:'flex', alignItems:'center', gap:4 }}><GameIcon name="cur_gems" size={14} /> {gems}</span>
            <span style={{ color:'#ff9955', display:'flex', alignItems:'center', gap:4 }}><GameIcon name="petFood" size={14} /> {petFood}</span>
          </div>
          <button onClick={onClose} style={{ padding:'6px 12px', fontSize:12 }}>CLOSE</button>
          {gainLine && <div className="clk-gain" data-testid="pet-gain">{gainLine}</div>}
        </div>

        {/* Nav */}
        <div style={{ display:'flex', gap:4, padding:'8px 12px', background:'#060410', borderBottom:'1px solid #1a0a2e' }}>
          {['my_pets','eggs','breed','shop'].map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{ fontFamily:'VT323', fontSize:14, letterSpacing:'0.15em', padding:'4px 14px',
                background: view === v ? 'var(--rune)' : 'transparent',
                border: `1px solid ${view===v?'var(--rune)':'#1a0a2e'}`, color: view===v?'#000':'var(--ink-dim)', borderRadius:2, cursor:'pointer' }}>
              {v==='my_pets'?'MY PETS':v==='eggs'?`EGGS (${eggs.length})`:v==='breed'?'BREED':'SHOP'}
            </button>
          ))}
          <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
            {onOpenTcg && (
              <button onClick={onOpenTcg} data-testid="pet-open-tcg" style={{ fontFamily:'VT323', fontSize:13, padding:'4px 12px',
                background:'#1a0a2e', border:'1px solid #ff7a1a55', color:'#ffb380', cursor:'pointer',
                display:'flex', alignItems:'center', gap:4 }}>
                <GameIcon name="ui_blade" size={13} /> CARDS &amp; BATTLE
              </button>
            )}
            <button onClick={buyFood} disabled={gems < 50} style={{ fontFamily:'VT323', fontSize:13, padding:'4px 12px',
              background:'#1a0a2e', border:'1px solid #4dffd455', color:'#4dffd4', cursor:'pointer',
              display:'flex', alignItems:'center', gap:4 }}>
              <GameIcon name="cur_gems" size={13} />50 → <GameIcon name="petFood" size={13} />×5
            </button>
          </div>
        </div>

        <div style={{ overflowY:'auto', flex:1, padding:12 }}>
          {/* ── MY PETS ── */}
          {view === 'my_pets' && (
            <>
              {pets.length === 0 ? (
                <div style={{ textAlign:'center', color:'var(--ink-dim)', fontFamily:'VT323', fontSize:16, padding:40 }}>
                  No pets yet. Buy an egg from the SHOP and hatch it!
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10 }}>
                  {pets.map(pet => {
                    const def = PET_TYPES.find(p => p.id === pet.type);
                    const stage = getStage(pet.level);
                    const xpNeeded = xpToLevel(pet.level);
                    const pct = Math.min(100, (pet.xp / xpNeeded) * 100);
                    const rarityColor = RARITY_COLORS[pet.rarity] || '#9a8fa6';
                    return (
                      <div key={pet.id} style={{
                        background: '#0a0520', border: `1px solid ${pet.active?rarityColor:'#1a0a2e'}`,
                        borderRadius:6, padding:12, boxShadow: pet.active ? `0 0 12px ${rarityColor}44` : 'none',
                      }} data-testid={`pet-${pet.id}`}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                          <div><GameIcon name={def?.icon || 'ui_pets'} size={28} /></div>
                          <div style={{ fontFamily:'VT323', fontSize:11, color:rarityColor, textTransform:'uppercase' }}>{pet.rarity}</div>
                        </div>
                        <div style={{ fontFamily:'VT323', fontSize:16, color:'var(--ink)', marginTop:4 }}>{pet.name}</div>
                        <div style={{ fontFamily:'VT323', fontSize:12, color:'var(--ink-dim)', marginBottom:4, display:'flex', alignItems:'center', gap:4 }}>
                          <GameIcon name={stage.icon} size={13} /> {stage.name} · Lv {pet.level}
                        </div>
                        {/* XP bar */}
                        <div style={{ height:4, background:'#1a0a2e', borderRadius:2, overflow:'hidden', marginBottom:6 }}>
                          <div style={{ width:`${pct}%`, height:'100%', background: rarityColor, transition:'width 0.3s' }} />
                        </div>
                        <div style={{ fontFamily:'VT323', fontSize:11, color:'var(--ink-dim)', marginBottom:8 }}>
                          XP: {pet.xp}/{xpNeeded} · {def?.type}
                        </div>
                        <div style={{ display:'flex', gap:6 }}>
                          <button onClick={() => feedPet(pet)} disabled={petFood <= 0 || pet.level >= 100}
                            style={{ flex:1, fontFamily:'VT323', fontSize:13, padding:'4px 8px',
                              background:'#1a0a2e', border:'1px solid #ff995544', color: petFood > 0 ? '#ff9955' : '#666', cursor:'pointer',
                              display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                            <GameIcon name="petFood" size={14} /> FEED
                          </button>
                          <button onClick={() => toggleActive(pet)}
                            style={{ flex:1, fontFamily:'VT323', fontSize:13, padding:'4px 8px',
                              background: pet.active ? rarityColor : '#1a0a2e',
                              border:`1px solid ${rarityColor}`, color: pet.active ? '#000' : rarityColor, cursor:'pointer' }}>
                            {pet.active ? '✓ ACTIVE' : 'SET'}
                          </button>
                        </div>
                        {pet.active && def && (
                          <div style={{ fontFamily:'VT323', fontSize:11, color:'#4dffd4', marginTop:4 }}>
                            +{(def.statPerLevel * pet.level * 100).toFixed(1)}% {def.stat}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ── EGGS ── */}
          {view === 'eggs' && (
            <>
              {/* Incubator */}
              <div className="inc-head">
                <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}><GameIcon name="ui_flame" size={15} /> INCUBATOR</span>
                <span className="inc-slots">{incSlots.length}/{maxIncSlots} slots</span>
              </div>
              <div className="inc-grid">
                {Array.from({ length: maxIncSlots }).map((_, i) => {
                  const slot = incSlots[i];
                  if (!slot) {
                    return <div key={`empty-${i}`} className="inc-slot inc-empty">
                      <span className="inc-empty-mark">＋</span>
                      <span className="inc-empty-txt">place an egg</span>
                    </div>;
                  }
                  const def = PET_TYPES.find(p => p.id === slot.type);
                  const rc = RARITY_COLORS[slot.rarity] || '#9a8fa6';
                  const rem = incubatorRemaining(slot, now);
                  const ready = slot.ready || rem <= 0;
                  const pct = Math.max(0, Math.min(1, 1 - rem / (slot.durMs || 1)));
                  const cost = incubatorSpeedCost(slot, now);
                  const cracks = cracking[slot.eggId] || 0;
                  const mm = Math.floor(rem / 60000), ss = Math.floor((rem % 60000) / 1000);
                  return (
                    <div key={slot.eggId} className={`inc-slot${ready ? ' is-ready' : ''}`} style={{ '--rc': rc }}>
                      <div className={`inc-egg${ready ? ' ready' : ''} crack-${cracks}`}
                           onClick={() => tapShell(slot)}
                           data-testid={`inc-egg-${slot.eggId}`}>
                        <span className="inc-egg-glyph"><GameIcon name="ui_egg" size={22} /></span>
                        {ready && cracks > 0 && <span className="inc-crack-count">{3 - cracks} more</span>}
                      </div>
                      <div className="inc-name" style={{ color: rc, display:'flex', alignItems:'center', justifyContent:'center', gap:3 }}><GameIcon name={def?.icon || 'ui_pets'} size={13} /> {slot.rarity}</div>
                      {ready ? (
                        <div className="inc-ready-txt">TAP 3× TO CRACK</div>
                      ) : (
                        <>
                          <div className="inc-bar"><div className="inc-fill" style={{ width: `${pct * 100}%` }} /></div>
                          <div className="inc-time">{mm}m {ss.toString().padStart(2, '0')}s</div>
                          <div className="inc-btns">
                            <button className="inc-btn inc-gem" disabled={(save.gems || 0) < cost.gems}
                                    onClick={() => speedUp(slot, 'gems')}><GameIcon name="cur_gems" size={12} /> {cost.gems}</button>
                            <button className="inc-btn inc-gold" disabled={(save.gold || 0) < cost.gold}
                                    onClick={() => speedUp(slot, 'gold')}><GameIcon name="gold" size={12} /> {cost.gold}</button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {eggs.length === 0 ? (
                <div style={{ textAlign:'center', color:'var(--ink-dim)', fontFamily:'VT323', fontSize:16, padding:40 }}>
                  No eggs. Visit the SHOP to buy some!
                </div>
              ) : (
                <>
                  <div style={{ fontFamily:'VT323', color:'var(--ink-dim)', fontSize:14, marginBottom:12 }}>
                    Tap an egg to put it in the incubator. Merge 3 of same rarity → higher rarity!
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:8 }}>
                    {eggs.map(egg => {
                      const def = PET_TYPES.find(p => p.id === egg.type);
                      const rc = RARITY_COLORS[egg.rarity] || '#9a8fa6';
                      const sameTypeEggs = eggs.filter(e => e.type === egg.type && e.rarity === egg.rarity);
                      return (
                        <div key={egg.id} data-testid={`egg-${egg.id}`}
                          style={{ background:'#0a0520', border:`1px solid ${rc}`, borderRadius:6, padding:10, textAlign:'center', cursor:'pointer' }}
                          onClick={() => incubate(egg)}>
                          <div style={{ display:'flex', justifyContent:'center' }}><GameIcon name="ui_egg" size={26} /></div>
                          <div style={{ fontFamily:'VT323', fontSize:13, color:rc }}>{egg.rarity}</div>
                          <div style={{ fontFamily:'VT323', fontSize:12, color:'var(--ink-dim)', display:'flex', alignItems:'center', justifyContent:'center', gap:3 }}><GameIcon name={def?.icon || 'ui_pets'} size={12} /> {def?.name}</div>
                          <div style={{ fontFamily:'VT323', fontSize:11, color: incSlots.length >= maxIncSlots ? '#7a6a8a' : '#4dffd4', marginTop:4 }}>
                            {incSlots.length >= maxIncSlots ? 'INCUBATOR FULL' : `INCUBATE · ${Math.round((INCUBATOR_MS[egg.rarity] || INCUBATOR_MS.common) * incubatorSpeedMult(save) / 60000)}m`}
                          </div>
                          {sameTypeEggs.length >= 3 && (
                            <button onClick={(e) => { e.stopPropagation(); doMerge(egg.rarity, egg.type); }}
                              style={{ marginTop:4, fontFamily:'VT323', fontSize:11, padding:'2px 8px',
                                background:'#b362ff22', border:'1px solid #b362ff', color:'#b362ff', cursor:'pointer' }}>
                              MERGE ×3
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── BREED ── */}
          {view === 'breed' && (
            <>
              <div style={{ fontFamily:'VT323', color:'var(--ink-dim)', fontSize:14, marginBottom:12 }}>
                Pick 2 Adult+ pets (Lv {BREED_MIN_LEVEL}+) of the same species. Breeding produces a new egg one rarity above your best parent — each parent needs {BREED_COOLDOWN_MS/60000} min to recover afterward.
              </div>
              {pets.filter(p => p.level >= BREED_MIN_LEVEL).length < 2 ? (
                <div style={{ textAlign:'center', color:'var(--ink-dim)', fontFamily:'VT323', fontSize:16, padding:40 }}>
                  Need at least 2 Adult+ pets. Feed your pets to level them up!
                </div>
              ) : (
                <>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:8, marginBottom: 14 }}>
                    {pets.map(pet => {
                      const def = PET_TYPES.find(p => p.id === pet.type);
                      const rc = RARITY_COLORS[pet.rarity] || '#9a8fa6';
                      const eligible = canBreedPet(pet);
                      const picked = breedPair.includes(pet.id);
                      const onCooldown = pet.breedReadyAt && Date.now() < pet.breedReadyAt;
                      return (
                        <div key={pet.id} data-testid={`breed-pick-${pet.id}`}
                          onClick={() => toggleBreedPick(pet)}
                          style={{
                            background: picked ? `${rc}22` : '#0a0520', border: `1px solid ${picked ? rc : '#1a0a2e'}`,
                            borderRadius: 6, padding: 10, textAlign: 'center', cursor: eligible ? 'pointer' : 'not-allowed',
                            opacity: eligible ? 1 : 0.4,
                          }}>
                          <div style={{ display:'flex', justifyContent:'center' }}><GameIcon name={def?.icon || 'ui_pets'} size={26} /></div>
                          <div style={{ fontFamily: 'VT323', fontSize: 12, color: rc }}>{pet.rarity}</div>
                          <div style={{ fontFamily: 'VT323', fontSize: 11, color: 'var(--ink-dim)' }}>{pet.name}</div>
                          <div style={{ fontFamily: 'VT323', fontSize: 10, color: '#4dffd4' }}>Lv {pet.level}</div>
                          {onCooldown && <div style={{ fontFamily: 'VT323', fontSize: 10, color: '#ff5566' }}>Resting…</div>}
                          {picked && <div style={{ fontFamily: 'VT323', fontSize: 11, color: rc }}>✓ SELECTED</div>}
                        </div>
                      );
                    })}
                  </div>
                  {breedPair.length === 2 && (() => {
                    const a = pets.find(p => p.id === breedPair[0]);
                    const resultRarity = breedResultRarity();
                    const cost = BREED_GEM_COST[a?.rarity] || 80;
                    const canAfford = gems >= cost;
                    return (
                      <div style={{ background: '#0a0520', border: `1px solid ${RARITY_COLORS[resultRarity]}`, borderRadius: 6, padding: 14, textAlign: 'center' }}>
                        <div style={{ fontFamily: 'VT323', fontSize: 14, color: 'var(--ink-dim)' }}>Result egg rarity:</div>
                        <div style={{ fontFamily: 'VT323', fontSize: 20, color: RARITY_COLORS[resultRarity], letterSpacing: '0.2em', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}><GameIcon name="ui_egg" size={22} /> {resultRarity?.toUpperCase()}</div>
                        <button onClick={doBreed} disabled={!canAfford} data-testid="breed-confirm"
                          style={{ marginTop: 8, fontFamily: 'VT323', fontSize: 14, padding: '6px 16px',
                            background: canAfford ? RARITY_COLORS[resultRarity] : '#1a0a2e',
                            border: `1px solid ${RARITY_COLORS[resultRarity]}`, color: canAfford ? '#000' : '#666', cursor: canAfford ? 'pointer' : 'default',
                            display:'inline-flex', alignItems:'center', gap:4 }}>
                          BREED — <GameIcon name="cur_gems" size={13} />{cost}
                        </button>
                      </div>
                    );
                  })()}
                </>
              )}
            </>
          )}

          {/* ── SHOP ── */}
          {view === 'shop' && (
            <>
              <div style={{ fontFamily:'VT323', color:'var(--ink-dim)', fontSize:14, marginBottom:12, display:'flex', alignItems:'center', gap:4 }}>
                Buy pet eggs with <GameIcon name="cur_gems" size={13} /> Gems. Higher rarity = stronger pet & higher base stats.
              </div>
              {PET_TYPES.map(petDef => (
                <div key={petDef.id} style={{ background:'#0a0520', border:'1px solid #1a0a2e', borderRadius:6, padding:12, marginBottom:10 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                    <GameIcon name={petDef.icon} size={28} />
                    <div>
                      <div style={{ fontFamily:'VT323', fontSize:16, color:'var(--ink)' }}>{petDef.name}</div>
                      <div style={{ fontFamily:'VT323', fontSize:12, color:'var(--ink-dim)' }}>{petDef.type} · {petDef.desc}</div>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    {RARITIES.map(r => {
                      const cost = petDef.eggCosts[r];
                      const canAfford = gems >= cost;
                      return (
                        <button key={r} onClick={() => buyEgg(petDef.id, r)} disabled={!canAfford}
                          data-testid={`buy-egg-${petDef.id}-${r}`}
                          style={{ fontFamily:'VT323', fontSize:12, padding:'4px 10px',
                            background: canAfford ? '#0a0520' : '#06030e',
                            border:`1px solid ${RARITY_COLORS[r]}${canAfford?'':'44'}`,
                            color: canAfford ? RARITY_COLORS[r] : '#444', cursor: canAfford ? 'pointer' : 'default' }}>
                          {r.toUpperCase()}<br/><GameIcon name="cur_gems" size={11} />{cost}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
